import { supabase } from '../lib/supabase';

export interface Offer {
  id: string;
  product_id: string;
  buyer_id: string;
  seller_id: string;
  offer_price: number;
  offer_message: string;
  status: 'pending' | 'accepted' | 'declined' | 'countered' | 'expired' | 'completed';
  expires_at: string;
  created_at: string;
  updated_at: string;
  documents: string[]; // Array of document URLs/addresses from user-documents storage bucket
  // User alias information
  seller?: { alias: string };
  buyer?: { alias: string };
}

export interface CreateOfferData {
  product_id: string;
  seller_id: string;
  offer_price: number;
  offer_message: string;
  documents?: string[]; // Optional array of document URLs for job applications
}

export interface UpdateOfferData {
  status?: 'pending' | 'accepted' | 'declined' | 'countered' | 'expired' | 'completed';
  offer_price?: number;
  offer_message?: string;
  documents?: string[]; // Optional array of document URLs for job applications
}

export class OffersService {
  // Create a new offer
  static async createOffer(offerData: CreateOfferData): Promise<Offer> {
    try {
      console.log('OffersService.createOffer - Starting with data:', offerData);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('OffersService.createOffer - User not authenticated');
        throw new Error('User not authenticated');
      }

      console.log('OffersService.createOffer - User authenticated:', user.id);

      // 0) First, check if product has any accepted offers (real-time check)
      const { data: acceptedOffers, error: acceptedOffersError } = await supabase
        .from('offers')
        .select('id, status')
        .eq('product_id', offerData.product_id)
        .eq('status', 'accepted')
        .limit(1);

      if (acceptedOffersError) {
        console.error('OffersService.createOffer - Error checking accepted offers:', acceptedOffersError);
        throw new Error('Failed to verify offer status. Please try again.');
      }

      if (acceptedOffers && acceptedOffers.length > 0) {
        throw new Error('This product already has an accepted offer and is no longer available.');
      }

      // 1) Fetch all offers by this user for this product
      const { data: existingList, error: findErr } = await supabase
        .from('offers')
        .select('*')
        .eq('product_id', offerData.product_id)
        .eq('buyer_id', user.id)
        .order('created_at', { ascending: false });

      if (findErr) {
        console.warn('OffersService.createOffer - Find existing error (continuing):', findErr);
      }

      // If any exist, pick the most recent to update and remove other duplicates (pending ones)
      if (existingList && existingList.length > 0) {
        const [mostRecent, ...others] = existingList;
        console.log('OffersService.createOffer - Found existing offers, keeping:', mostRecent?.id, 'removing pending duplicates count:', others.length);

        // First, clean up any other pending duplicates
        if (others.length > 0) {
          const otherIds = others.map(o => o.id);
          try {
            await supabase
              .from('offers')
              .delete()
              .in('id', otherIds)
              .eq('status', 'pending');
            console.log('OffersService.createOffer - Cleaned up', others.length, 'duplicate offers');
          } catch (cleanupErr) {
            console.warn('OffersService.createOffer - Cleanup of duplicates failed:', cleanupErr);
          }
        }

        // Update the most recent offer
        const updatePayload: UpdateOfferData = {
          offer_price: offerData.offer_price,
          offer_message: offerData.offer_message,
          status: 'pending',
          documents: offerData.documents || [],
        };
        
        try {
          const updated = await this.updateOffer(mostRecent.id, updatePayload);
          console.log('OffersService.createOffer - Successfully updated existing offer:', updated);
          return updated;
        } catch (updateError) {
          console.error('OffersService.createOffer - Update failed, trying to create new offer:', updateError);
          // If update fails, continue to create a new offer
        }
      }

      // 2) Otherwise insert a new offer
      const insertData = {
        product_id: offerData.product_id,
        buyer_id: user.id,
        seller_id: offerData.seller_id,
        offer_price: offerData.offer_price,
        offer_message: offerData.offer_message,
        documents: offerData.documents || [],
      };

      console.log('OffersService.createOffer - Inserting data:', insertData);

      const { data, error } = await supabase
        .from('offers')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('OffersService.createOffer - Supabase error:', error);
        // Provide more specific error messages
        if (error.code === '23505') {
          // This should rarely happen now since we clean up duplicates first
          console.warn('OffersService.createOffer - Duplicate key error, this might indicate a race condition');
          throw new Error('A duplicate offer was detected. Please try again in a moment.');
        } else if (error.code === '23503') {
          throw new Error('Invalid product or seller information. Please refresh the page and try again.');
        } else if (error.code === '23514') {
          // Check if this is a job application (offer_price = 0)
          if (insertData.offer_price === 0) {
            throw new Error('Job application failed due to database constraints. Please ensure your cover letter is at least 10 characters long and try again.');
          } else {
            throw new Error('Invalid offer data. Please check your offer price and message.');
          }
        } else {
          throw new Error(`Failed to create offer: ${error.message || 'Unknown error'}`);
        }
      }

      console.log('OffersService.createOffer - Success:', data);
      return data;
    } catch (error) {
      console.error('OffersService.createOffer - Error:', error);
      // Re-throw with more context if it's not already a user-friendly error
      if (error instanceof Error && !error.message.includes('Failed to create offer') && !error.message.includes('You have already made an offer') && !error.message.includes('Invalid')) {
        throw new Error(`Failed to create offer: ${error.message}`);
      }
      throw error;
    }
  }

  // Get offers for a specific product
  static async getOffersForProduct(productId: string): Promise<Offer[]> {
    try {
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching offers for product:', error);
      throw error;
    }
  }

  // Get offers made by the current user (as buyer)
  static async getMyOffers(): Promise<Offer[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('offers')
        .select(`
          *,
          seller:users!offers_seller_id_fkey(alias)
        `)
        .eq('buyer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching my offers:', error);
      throw error;
    }
  }

  // Get offers received by the current user (as seller)
  static async getOffersReceived(): Promise<Offer[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('offers')
        .select(`
          *,
          buyer:users!offers_buyer_id_fkey(alias)
        `)
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching received offers:', error);
      throw error;
    }
  }

  // Update an offer (for status changes, price updates, etc.)
  static async updateOffer(offerId: string, updateData: UpdateOfferData): Promise<Offer> {
    try {
      const { data, error } = await supabase
        .from('offers')
        .update(updateData)
        .eq('id', offerId)
        .select()
        .single();

      if (error) {
        console.error('OffersService.updateOffer - Supabase error:', error);
        if (error.code === 'PGRST116') {
          throw new Error('Offer not found. It may have been deleted or you may not have permission to update it.');
        } else if (error.code === '23514') {
          throw new Error('Invalid offer data. Please check your offer price and message.');
        } else {
          throw new Error(`Failed to update offer: ${error.message || 'Unknown error'}`);
        }
      }
      
      return data;
    } catch (error) {
      console.error('Error updating offer:', error);
      // Re-throw with more context if it's not already a user-friendly error
      if (error instanceof Error && !error.message.includes('Failed to update offer') && !error.message.includes('Offer not found') && !error.message.includes('Invalid')) {
        throw new Error(`Failed to update offer: ${error.message}`);
      }
      throw error;
    }
  }

  // Accept an offer and create a purchase record, then mark offer completed
  static async acceptOffer(offerId: string): Promise<Offer> {
    // Step 1: Mark offer as accepted (visible to both parties immediately)
    const accepted = await this.updateOffer(offerId, { status: 'accepted' });

    try {
      // Step 2: Create purchase from this offer (snapshot product + link users)
      const { PurchasesService } = await import('./purchasesService');
      await PurchasesService.createFromOffer(offerId);

      // Step 3: Update offer to completed to lock status
      const completed = await this.updateOffer(offerId, { status: 'completed' });

      // Step 4: If product is a private single-buy, mark product inactive and sold
      try {
        // Load the offer with product_id
        const { data: offerRow, error: offerErr } = await supabase
          .from('offers')
          .select('product_id')
          .eq('id', offerId)
          .single();

        if (!offerErr && offerRow?.product_id) {
          const productId = offerRow.product_id;

          // Check product flag
          const { data: product, error: productErr } = await supabase
            .from('products')
            .select('id, is_private_singlebuy')
            .eq('id', productId)
            .single();

          if (!productErr && product?.is_private_singlebuy) {
            const { error: updateErr } = await supabase
              .from('products')
              .update({ is_active: false, is_sold: true })
              .eq('id', productId);

            if (updateErr) {
              console.warn('OffersService.acceptOffer - Failed to mark product inactive/sold:', updateErr);
            }
          }

          // Always increment number_sold for any accepted offer (client-side)
          try {
            const { data: currentProduct, error: fetchErr } = await supabase
              .from('products')
              .select('number_sold')
              .eq('id', productId)
              .single();

            if (!fetchErr && currentProduct) {
              const newCount = (currentProduct.number_sold || 0) + 1;
              const { error: updateErr } = await supabase
                .from('products')
                .update({ number_sold: newCount })
                .eq('id', productId);

              if (updateErr) {
                console.error('OffersService.acceptOffer - Failed to increment sold count:', updateErr);
              } else {
                console.log('OffersService.acceptOffer - Successfully incremented sold count to:', newCount);
              }
            } else {
              console.error('OffersService.acceptOffer - Failed to fetch current product:', fetchErr);
            }
          } catch (incrementError) {
            console.error('OffersService.acceptOffer - Error incrementing sold count:', incrementError);
          }
        }
      } catch (innerErr) {
        console.warn('OffersService.acceptOffer - Post-accept product update failed:', innerErr);
      }
      return completed;
    } catch (err) {
      console.error('OffersService.acceptOffer - Failed to finalize purchase:', err);
      // If purchase creation fails, log detailed error and rethrow to notify caller
      const errorMessage = err instanceof Error ? err.message : 'Failed to create purchase record';
      console.error('OffersService.acceptOffer - Purchase creation error details:', {
        offerId,
        error: err,
        message: errorMessage
      });
      // Re-throw the error so the UI knows purchase creation failed
      throw new Error(`Failed to create purchase: ${errorMessage}`);
    }
  }

  // Decline an offer
  static async declineOffer(offerId: string): Promise<Offer> {
    return this.updateOffer(offerId, { status: 'declined' });
  }

  // Counter an offer (create a new offer with different price)
  static async counterOffer(offerId: string, newPrice: number, newMessage: string): Promise<Offer> {
    try {
      // First, mark the original offer as countered
      await this.updateOffer(offerId, { status: 'countered' });

      // Get the original offer to create a counter offer
      const { data: originalOffer, error: fetchError } = await supabase
        .from('offers')
        .select('*')
        .eq('id', offerId)
        .single();

      if (fetchError) throw fetchError;

      // Create a new counter offer (seller becomes buyer, buyer becomes seller)
      const counterOfferData: CreateOfferData = {
        product_id: originalOffer.product_id,
        seller_id: originalOffer.buyer_id, // Original buyer becomes seller
        offer_price: newPrice,
        offer_message: newMessage,
      };

      return this.createOffer(counterOfferData);
    } catch (error) {
      console.error('Error countering offer:', error);
      throw error;
    }
  }

  // Delete an offer (buyers can delete their own offers regardless of status)
  static async deleteOffer(offerId: string): Promise<void> {
    try {
      console.log('OffersService.deleteOffer - Simplified: deleting ALL offers');

      // Note: PostgREST requires a filter for DELETE; use a not-null filter to target all rows
      const { error } = await supabase
        .from('offers')
        .delete()
        .not('id', 'is', null);

      if (error) {
        console.error('OffersService.deleteOffer - Bulk delete error:', error);
        throw new Error(error.message || 'Failed to delete offers');
      }

      console.log('OffersService.deleteOffer - All offers deleted');
    } catch (error) {
      console.error('OffersService.deleteOffer - Final error:', error);
      throw error;
    }
  }

  // Get offer by ID
  static async getOfferById(offerId: string): Promise<Offer | null> {
    try {
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('id', offerId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error fetching offer by ID:', error);
      throw error;
    }
  }

  // Check if user has already made an offer for a product
  static async hasUserOfferedForProduct(productId: string, userId?: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const buyerId = userId || user?.id;
      
      if (!buyerId) return false;

      const { data, error } = await supabase
        .from('offers')
        .select('id')
        .eq('product_id', productId)
        .eq('buyer_id', buyerId)
        .eq('status', 'pending')
        .limit(1);

      if (error) {
        console.error('Error checking if user has offered:', error);
        return false;
      }
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking if user has offered:', error);
      return false;
    }
  }

  // Check if an offer can be made for a product (product exists, is active, no accepted offers, etc.)
  static async canMakeOfferForProduct(productId: string, userId?: string): Promise<{
    canMakeOffer: boolean;
    reason?: string;
    acceptedOfferPrice?: number;
    productInfo?: {
      id: string;
      name: string;
      price: number;
      seller_id: string;
      is_active: boolean;
    };
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const buyerId = userId || user?.id;
      
      if (!buyerId) {
        return { canMakeOffer: false, reason: 'User not authenticated' };
      }

      // 1. Check if product exists and is active
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('id, name, price, seller_id, is_active')
        .eq('id', productId)
        .single();

      if (productError) {
        if (productError.code === 'PGRST116') {
          return { canMakeOffer: false, reason: 'Product not found or has been deleted' };
        }
        console.error('Error fetching product:', productError);
        return { canMakeOffer: false, reason: 'Failed to verify product information' };
      }

      if (!productData) {
        return { canMakeOffer: false, reason: 'Product not found' };
      }

      // 2. Check if there are any accepted offers for this product (even if product is inactive)
      const { data: acceptedOffers, error: offersError } = await supabase
        .from('offers')
        .select('id, status, offer_price')
        .eq('product_id', productId)
        .in('status', ['accepted', 'completed'])
        .limit(1);

      if (offersError) {
        console.error('Error checking accepted offers:', offersError);
        return { canMakeOffer: false, reason: 'Failed to check offer status' };
      }

      const acceptedOfferPrice = (acceptedOffers && acceptedOffers.length > 0)
        ? ((acceptedOffers?.[0] as any)?.offer_price ?? undefined)
        : undefined;

      if (acceptedOffers && acceptedOffers.length > 0) {
        return {
          canMakeOffer: false,
          reason: 'This product already has an accepted offer',
          acceptedOfferPrice,
          productInfo: productData
        };
      }

      // 3. Check if product is active
      if (!productData.is_active) {
        return { 
          canMakeOffer: false, 
          reason: 'This product is no longer available',
          acceptedOfferPrice,
          productInfo: productData
        };
      }

      // 4. Check if user is trying to make an offer on their own product
      if (productData.seller_id === buyerId) {
        return { 
          canMakeOffer: false, 
          reason: 'You cannot make an offer on your own product',
          productInfo: productData
        };
      }

      // 5. Check if user already has a pending offer for this product
      // Note: We allow users to make new offers even if they have pending ones
      // This allows them to update their existing offer
      const { data: userOffers, error: userOffersError } = await supabase
        .from('offers')
        .select('id, status')
        .eq('product_id', productId)
        .eq('buyer_id', buyerId)
        .eq('status', 'pending')
        .limit(1);

      if (userOffersError) {
        console.error('Error checking user offers:', userOffersError);
        return { canMakeOffer: false, reason: 'Failed to check your existing offers' };
      }

      // Note: We don't block offers if user has pending offers - they can update them
      // So we continue to return canMakeOffer: true even if they have pending offers

      return { 
        canMakeOffer: true,
        productInfo: productData
      };

    } catch (error) {
      console.error('Error checking if offer can be made:', error);
      return { canMakeOffer: false, reason: 'Failed to validate offer eligibility' };
    }
  }

  // Get offer statistics for a user
  static async getOfferStats(userId?: string): Promise<{
    totalOffers: number;
    pendingOffers: number;
    acceptedOffers: number;
    declinedOffers: number;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) {
        return { totalOffers: 0, pendingOffers: 0, acceptedOffers: 0, declinedOffers: 0 };
      }

      const { data, error } = await supabase
        .from('offers')
        .select('status')
        .eq('buyer_id', targetUserId);

      if (error) throw error;

      const stats = {
        totalOffers: data?.length || 0,
        pendingOffers: data?.filter(o => o.status === 'pending').length || 0,
        acceptedOffers: data?.filter(o => o.status === 'accepted').length || 0,
        declinedOffers: data?.filter(o => o.status === 'declined').length || 0,
      };

      return stats;
    } catch (error) {
      console.error('Error fetching offer stats:', error);
      return { totalOffers: 0, pendingOffers: 0, acceptedOffers: 0, declinedOffers: 0 };
    }
  }

  // Create a job application (specialized version of createOffer for job applications)
  static async createJobApplication(applicationData: {
    product_id: string;
    seller_id: string;
    coverLetter: string;
    personalInfo: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      linkedin?: string;
      portfolio?: string;
    };
    experience: string;
    education: string;
    skills: string[];
    motivation: string;
    documents: string[]; // Array of document URLs from user-documents bucket
  }): Promise<Offer> {
    try {
      console.log('OffersService.createJobApplication - Starting with data:', applicationData);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('OffersService.createJobApplication - User not authenticated');
        throw new Error('User not authenticated');
      }

      // Create a comprehensive offer message that includes all application details
      const offerMessage = `
Job Application for ${applicationData.personalInfo.first_name} ${applicationData.personalInfo.last_name}

Contact Information:
- Email: ${applicationData.personalInfo.email}
- Phone: ${applicationData.personalInfo.phone || 'Not provided'}
- LinkedIn: ${applicationData.personalInfo.linkedin || 'Not provided'}
- Portfolio: ${applicationData.personalInfo.portfolio || 'Not provided'}

Cover Letter:
${applicationData.coverLetter || 'No cover letter provided'}

Experience:
${applicationData.experience || 'No experience provided'}

Education:
${applicationData.education || 'No education provided'}

Skills:
${applicationData.skills.join(', ') || 'No skills listed'}

Motivation:
${applicationData.motivation || 'No motivation provided'}
      `.trim();

      // Ensure the message meets minimum length requirements
      if (offerMessage.length < 10) {
        throw new Error('Job application message is too short. Please provide more details in your application.');
      }

      // Use the existing createOffer method with job application data
      const offerData: CreateOfferData = {
        product_id: applicationData.product_id,
        seller_id: applicationData.seller_id,
        offer_price: 0, // Job applications don't have a price, use 0
        offer_message: offerMessage,
        documents: applicationData.documents,
      };

      console.log('OffersService.createJobApplication - Creating offer with data:', offerData);
      let result: Offer;
      try {
        result = await this.createOffer(offerData);
      } catch (firstErr: any) {
        // If legacy DB constraints (price > 0 or min length) still exist, retry with safe values
        const message = (offerMessage.length < 10)
          ? `${offerMessage}\n\n(autofill)` // ensure length >= 10
          : offerMessage;
        const fallbackOfferData: CreateOfferData = {
          ...offerData,
          offer_price: 0.01, // minimally positive to bypass strict price>0 constraints
          offer_message: message,
        };
        console.warn('OffersService.createJobApplication - Primary insert failed, retrying with fallback:', firstErr?.message || firstErr);
        result = await this.createOffer(fallbackOfferData);
      }
      
      console.log('OffersService.createJobApplication - Success:', result);
      return result;
    } catch (error) {
      console.error('OffersService.createJobApplication - Error:', error);
      throw error;
    }
  }
}
